import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import PlanificationTypeService from '../../services/planificationType.service';

const DeletedPlanificationTypesList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchDeleted();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchDeleted = async () => {
    setLoading(true);
    try {
      const res = await PlanificationTypeService.getTrashed();
      setItems(res.data?.data || []);
    } catch (e) {
      console.error('Error loading deleted planification types:', e);
      showSnackbar('Error loading deleted types', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmRestore = async () => {
    if (!restoreDialog.item) return;
    try {
      await PlanificationTypeService.restore(restoreDialog.item.id);
      showSnackbar('Type restored successfully');
      fetchDeleted();
    } catch (e) {
      console.error('Error restoring type:', e);
      showSnackbar('Error restoring type', 'error');
    } finally {
      setRestoreDialog({ open: false, item: null });
    }
  };

  const filtered = items.filter(i => (i.name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/planifications/types')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{t('planifications.deletedTypesTitle')}</Typography>
            </Box>
          </Box>

          <TextField
            placeholder={t('planifications.searchDeletedTypes')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 320 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('planifications.deletedAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('planifications.noDeletedTypesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{formatDate(item.deleted_at)}</TableCell>
                      <TableCell align="center">
                        {hasPermission('planification-types.edit') && (
                          <IconButton
                            size="small"
                            color="primary"
                            title={t('planifications.restore')}
                            onClick={() => setRestoreDialog({ open: true, item })}
                          >
                            <RestoreIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false, item: null })}>
        <DialogTitle>{t('planifications.restoreTypeTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('planifications.restoreConfirm', { name: restoreDialog.item?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, item: null })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={confirmRestore}>{t('planifications.restore')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedPlanificationTypesList;
